import React from "react";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import { publishTemplate } from "./esourceService";

function buildPublishConfirmMessage({
  draftVersionNo,
  publishedVersionNo,
  formName,
  formCode,
} = {}) {
  const draftLabel =
    draftVersionNo != null && draftVersionNo !== ""
      ? `v${draftVersionNo}`
      : null;
  const liveLabel =
    publishedVersionNo != null && publishedVersionNo !== ""
      ? `v${publishedVersionNo}`
      : null;
  const formLabel =
    [formCode, formName].filter(Boolean).join(" — ") || "this form";

  return (
    <div className="esource-publish-confirm">
      <p className="esource-publish-confirm__lead">
        You are about to publish{" "}
        {draftLabel ? (
          <>
            draft <strong>{draftLabel}</strong>
          </>
        ) : (
          "the current draft"
        )}{" "}
        of <strong>{formLabel}</strong>.
      </p>
      <p className="esource-publish-confirm__effects-title">
        What happens if you publish:
      </p>
      <ul className="esource-publish-confirm__effects">
        <li>
          {draftLabel ? (
            <>
              Draft <strong>{draftLabel}</strong> becomes the live published
              version.
            </>
          ) : (
            <>This draft becomes the live published version.</>
          )}
        </li>
        <li>
          New form records will use this published version going forward.
        </li>
        {liveLabel ? (
          <li>
            The previous live version (<strong>{liveLabel}</strong>) will no
            longer be used for new records. Existing records already created
            against older versions are not changed.
          </li>
        ) : (
          <li>
            This will be the first published version of the form for new
            records.
          </li>
        )}
        <li>
          After publishing you cannot edit this version directly. Create a new
          draft version from Form Management to make further changes.
        </li>
      </ul>
      <p className="esource-publish-confirm__next">
        If you continue, you will confirm your identity and provide a publish
        reason.
      </p>
    </div>
  );
}

/**
 * Ask for publish confirmation (version + effects), then password/PIN + reason.
 * @param {string} formTemplateId
 * @param {{
 *   showAlert?: Function,
 *   draftVersionNo?: number|string|null,
 *   publishedVersionNo?: number|string|null,
 *   formName?: string,
 *   formCode?: string,
 * }} [options]
 */
export async function confirmAndPublishTemplate(formTemplateId, options = {}) {
  if (!formTemplateId) {
    return {
      success: false,
      cancelled: false,
      message: "Form template is required.",
    };
  }

  const {
    showAlert,
    draftVersionNo,
    publishedVersionNo,
    formName,
    formCode,
  } = options;

  if (typeof showAlert === "function") {
    const confirmed = await new Promise((resolve) => {
      showAlert({
        title: "Publish this form version?",
        message: buildPublishConfirmMessage({
          draftVersionNo,
          publishedVersionNo,
          formName,
          formCode,
        }),
        type: "warning",
        buttonText: "Continue to publish",
        cancelText: "Cancel",
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) {
      return {
        success: false,
        cancelled: true,
        message: "Publish cancelled.",
      };
    }
  }

  const authRes = await openAuthModal({
    showDescription: true,
    requireDescription: true,
    descriptionLabel: "Reason for publishing",
    descriptionPlaceholder: "Why are you publishing this form version?",
    introMessage: draftVersionNo != null
      ? `Confirm your identity and provide a reason to publish draft v${draftVersionNo}.`
      : "Confirm your identity and provide a reason to publish this form.",
  });

  if (authRes?.Success !== 1) {
    return {
      success: false,
      cancelled: Boolean(authRes?.cancelled),
      message: authRes?.cancelled
        ? "Publish cancelled."
        : "Authentication failed or was cancelled.",
    };
  }

  const reason = String(authRes?.Description || "").trim();
  if (!reason) {
    return {
      success: false,
      cancelled: false,
      message: "A reason is required to publish this form.",
    };
  }

  return publishTemplate(formTemplateId, { Reason: reason });
}
